import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categoryData, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : null;

  if (!category) {
    return <NotFound />;
  }

  const categoryArticles = getArticlesByCategory(category.id);

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg
        type="WebPage"
        extra={{ name: `${category.name} - 久久网`, description: category.description }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        extra={{
          items: [
            { name: '首页', url: 'https://njqxb.cn/' },
            { name: category.name, url: `https://njqxb.cn/category/${slug}` },
          ],
        }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { name: '首页', url: '/' },
                { name: category.name },
              ]}
            />

            <div className="mt-6 mb-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-primary-100/70 flex items-center justify-center">
                  <i className={`${category.icon} text-2xl text-primary-500`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-[var(--font-heading)]">
                    {category.name}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
                </div>
              </div>
            </div>

            <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
              {categoryData.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className={`flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap no-underline ${
                    cat.id === category.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-white text-foreground-600 border border-background-200/70 hover:border-primary-200 hover:text-primary-500'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 pb-16">
                {categoryArticles.map((article) => (
                  <article
                    key={article.id}
                    className="bg-white rounded-xl border border-background-200/70 overflow-hidden group transition-all duration-300 hover:border-primary-200"
                  >
                    <Link to={`/article/${article.slug}`} className="block no-underline">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-5">
                        <h2 className="text-base md:text-lg font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors">
                          {article.title}
                        </h2>
                        <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-4">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <i className="ri-calendar-line"></i>
                              {article.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line"></i>
                              {article.views.toLocaleString()}
                            </span>
                          </div>
                          <span className="flex items-center gap-1 text-primary-500 font-medium">
                            阅读更多
                            <i className="ri-arrow-right-line"></i>
                          </span>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}